import {
  AgendaClassificacaoRequest,
  AgendaConvenioRequest,
  AgendaEspecialidadeDaAgenda,
  AgendaEspecialidadeRequest,
  AgendaGradeLinha,
  AgendaObservacaoRequest,
  AgendaPlanoPaciente,
  AgendaProcedimentoRequest,
  AgendaStatusRequest,
  AgendaTipoAtendimentoRequest,
  AgendaTipoConsultaRequest,
} from '../../../../interfaces/agenda';
import { STATUS_EM_ANDAMENTO, tipoAgendamento } from '../agenda-acoes';
import { CONVENIO_CARTAO } from '../agenda-agendamento/agenda-agendamento';
import { temPaciente } from '../agenda-grade/agenda-grade';

/**
 * Ordem de aplicação em cada linha. Especialidade e procedimento vão antes do status: se o
 * status mudar para "em andamento" primeiro, o back recusa a troca depois.
 */
export const ORDEM_CAMPOS = [
  'especialidade', 'procedimento', 'status', 'classificacao', 'tipoAtendimento', 'tipoConsulta', 'convenio', 'observacao',
] as const;
export type CampoLote = typeof ORDEM_CAMPOS[number];

export const ROTULO_CAMPO: Record<CampoLote, string> = {
  especialidade: 'Especialidade',
  procedimento: 'Procedimento',
  status: 'Status',
  classificacao: 'Classificação',
  tipoAtendimento: 'Tipo de atendimento',
  tipoConsulta: 'Tipo de consulta',
  convenio: 'Convênio',
  observacao: 'Observação',
};

/** Lista do original. Cancelada fica de fora: cancelar pede motivo e regera o horário. */
export const STATUS_LOTE: { valor: string; rotulo: string }[] = [
  { valor: 'N', rotulo: 'Normal' }, { valor: 'CN', rotulo: 'Confirmada' },
  { valor: 'AR', rotulo: 'Aguardando pagamento' }, { valor: 'AC', rotulo: 'Aguardando consulta (pago)' },
  { valor: 'O', rotulo: 'Em Consulta' }, { valor: 'AD', rotulo: 'Atendido' }, { valor: 'E', rotulo: 'Executada' },
  { valor: 'M', rotulo: 'Marcada' }, { valor: 'IN', rotulo: 'Iniciada' }, { valor: 'FI', rotulo: 'Finalizado' },
  { valor: 'B', rotulo: 'Bloqueada' }, { valor: 'F', rotulo: 'Falta justificada' }, { valor: 'I', rotulo: 'Falta não justificada' },
  { valor: 'RE', rotulo: 'Remarcada' }, { valor: 'S', rotulo: 'Suspenso' },
];
export const STATUS_RESERVADO = 'R';

/** NR_SEQ_TIPO_CONSULTA: o original só oferece "Reagendamento Online" ou limpar. */
export const TIPO_CONSULTA_REAGENDAMENTO_ONLINE = 4;
export type EscolhaTipoConsulta = 'manter' | 'limpar' | 'reagendamento';

export interface EstadoLote {
  status: string | null;
  /** A sigla que a gravação aceita. */
  classif: string | null;
  cdConvenio: number | null;
  /** Ignorada no 224: cada paciente fica com a categoria do próprio plano. */
  cdCategoria: string | null;
  cdEspecialidade: number | null;
  nrSeqProcInterno: number | null;
  tipoAtendimento: number | null;
  tipoConsulta: EscolhaTipoConsulta;
  alterarObservacao: boolean;
  observacao: string;
}

export function estadoLoteInicial(): EstadoLote {
  return {
    status: null, classif: null, cdConvenio: null, cdCategoria: null, cdEspecialidade: null, nrSeqProcInterno: null,
    tipoAtendimento: null, tipoConsulta: 'manter', alterarObservacao: false, observacao: '',
  };
}

export interface ContextoLote {
  supervisor: boolean;
  /** Plano do cartão por paciente (só com o 224 escolhido). Ausente = não consultado; null = falhou. */
  planos: Record<string, AgendaPlanoPaciente | null>;
}

export type TipoSelecao = 'CONSULTA' | 'EXAME' | 'misto' | null;

export function tipoSelecao(linhas: AgendaGradeLinha[]): TipoSelecao {
  const tipos = new Set(linhas.map(tipoAgendamento));
  if (!tipos.size) return null;
  return tipos.size > 1 ? 'misto' : [...tipos][0];
}

/** Espelho de CarregarSlotParaTrocaProc do back: quando procedimento e especialidade não trocam. */
export function motivoTravaSlot(l: AgendaGradeLinha): string | null {
  if (!temPaciente(l)) return 'Horário livre — agende o paciente primeiro.';
  if (l.ieStatusAgenda === 'C') return 'Agendamento cancelado.';
  if (l.paciente?.nrAtendimento) {
    return `Atendimento ${l.paciente.nrAtendimento} já aberto — trocar aqui não mexe nos procedimentos já lançados na conta.`;
  }
  if (STATUS_EM_ANDAMENTO.includes(l.ieStatusAgenda ?? '')) {
    return `Agendamento com status "${l.dsStatusAgenda || l.ieStatusAgenda}".`;
  }
  return null;
}

/** Trava da linha inteira: horário reservado só a supervisão altera. */
export function motivoLinhaLote(l: AgendaGradeLinha, supervisor: boolean): string | null {
  return l.ieStatusAgenda === STATUS_RESERVADO && !supervisor ? 'Reservado: só supervisão altera' : null;
}

export interface TravaLinha {
  tom: 'bad' | 'warn';
  texto: string;
}

/** Selos da coluna "Travas" da tabela do modal. */
export function travasLinha(l: AgendaGradeLinha, supervisor: boolean): TravaLinha[] {
  const t: TravaLinha[] = [];
  const geral = motivoLinhaLote(l, supervisor);
  if (geral) t.push({ tom: 'bad', texto: geral });
  if (STATUS_EM_ANDAMENTO.includes(l.ieStatusAgenda ?? '')) {
    t.push({ tom: 'warn', texto: `${l.dsStatusAgenda || l.ieStatusAgenda}: não troca especialidade nem procedimento` });
  } else if (l.paciente?.nrAtendimento) {
    t.push({ tom: 'warn', texto: `Com atendimento ${l.paciente.nrAtendimento}: não troca procedimento nem especialidade` });
  }
  return t;
}

/**
 * Trava do campo para a seleção inteira; null = o campo pode ser usado. Procedimento e
 * especialidade dependem da agenda do agendamento, por isso exigem uma agenda só (procedimento,
 * como no original, um agendamento só).
 */
export function motivoCampoSelecao(campo: CampoLote, linhas: AgendaGradeLinha[]): string | null {
  const tipo = tipoSelecao(linhas);
  switch (campo) {
    case 'classificacao':
      return tipo === 'misto' ? 'Seleção mistura consulta e exame' : null;
    case 'tipoAtendimento':
    case 'tipoConsulta':
      return linhas.some(l => tipoAgendamento(l) === 'CONSULTA') ? null : 'Só em consulta';
    case 'especialidade': {
      if (tipo !== 'CONSULTA') return 'Só em consulta';
      if (new Set(linhas.map(l => l.cdAgenda)).size > 1) return 'Selecionados de agendas diferentes';
      const travas = linhas.map(motivoTravaSlot);
      if (travas.every(Boolean)) return linhas.length === 1 ? travas[0] : 'Nenhum selecionado aceita troca de especialidade';
      return null;
    }
    case 'procedimento':
      if (tipo !== 'EXAME') return 'Só em agenda de exame';
      if (linhas.length > 1) return 'Troca de exame só com 1 selecionado';
      return motivoTravaSlot(linhas[0]);
    default:
      return null;
  }
}

/**
 * Trava que depende da lista de especialidades da agenda. Agenda com uma só ainda serve para
 * corrigir linha gravada com outra especialidade (dado antigo); só trava se todas já estão nela.
 */
export function motivoEspecialidadeDaAgenda(lista: AgendaEspecialidadeDaAgenda[], linhas: AgendaGradeLinha[]): string | null {
  if (!lista.length) return 'Agenda sem especialidade cadastrada';
  if (lista.length > 1) return null;
  const unica = lista[0].cdEspecialidade;
  const aptas = linhas.filter(l => !motivoTravaSlot(l));
  return aptas.every(l => l.especialidade?.cdEspecialidadeAgend === unica) ? `A agenda atende só ${lista[0].dsEspecialidade}` : null;
}

/**
 * Observação em lote marcada e vazia apaga a de cada linha que tem texto: conta essas linhas para
 * pedir confirmação antes (no original, vazio com vários era "manter").
 */
export function linhasQueApagamObservacao(linhas: AgendaGradeLinha[], e: EstadoLote, ctx: ContextoLote): number {
  if (linhas.length < 2 || !e.alterarObservacao || e.observacao.trim()) return 0;
  return linhas.filter(l => passosDaLinha(l, e, ctx).some(p => p.campo === 'observacao' && p.corpo)).length;
}

/** Plano exigido pelo 224 para a linha; motivo quando ela vai ser pulada. */
export function planoDaLinha224(l: AgendaGradeLinha, planos: ContextoLote['planos']): { cdCategoria: string | null; motivo: string | null } {
  const cd = (l.paciente?.cdPessoaFisica ?? '').trim();
  if (!(cd in planos)) return { cdCategoria: null, motivo: 'Plano do cartão não consultado' };
  const p = planos[cd];
  if (p === null) return { cdCategoria: null, motivo: 'Não foi possível ler o plano do cartão' };
  if (!p.temPlano || p.cdConvenio !== CONVENIO_CARTAO || !p.cdCategoria) return { cdCategoria: null, motivo: 'Paciente sem plano do cartão' };
  return { cdCategoria: String(p.cdCategoria), motivo: null };
}

export function camposEscolhidos(e: EstadoLote): CampoLote[] {
  const marcado: Record<CampoLote, boolean> = {
    especialidade: e.cdEspecialidade != null,
    procedimento: e.nrSeqProcInterno != null,
    status: !!e.status,
    classificacao: !!e.classif,
    tipoAtendimento: e.tipoAtendimento != null,
    tipoConsulta: e.tipoConsulta !== 'manter',
    convenio: e.cdConvenio != null,
    observacao: e.alterarObservacao,
  };
  return ORDEM_CAMPOS.filter(c => marcado[c]);
}

/** O que impede aplicar; vazio = pode. */
export function faltandoLote(e: EstadoLote, carregandoPlanos: boolean): string[] {
  const f: string[] = [];
  if (!camposEscolhidos(e).length) f.push('preencha ao menos um campo');
  if (e.cdConvenio != null && e.cdConvenio !== CONVENIO_CARTAO && !e.cdCategoria) f.push('categoria');
  if (e.cdConvenio === CONVENIO_CARTAO && carregandoPlanos) f.push('planos do cartão');
  return f;
}

export type CorpoLote =
  | AgendaStatusRequest
  | AgendaClassificacaoRequest
  | AgendaConvenioRequest
  | AgendaTipoAtendimentoRequest
  | AgendaTipoConsultaRequest
  | AgendaProcedimentoRequest
  | AgendaEspecialidadeRequest
  | AgendaObservacaoRequest;

/** Um campo numa linha: com corpo vai para a rota; com motivo é pulado (igual = nada mudaria). */
export interface PassoLote {
  campo: CampoLote;
  corpo: CorpoLote | null;
  motivo: string | null;
  igual?: boolean;
}

/** Os PUTs de uma linha, na ordem de aplicação, com o corpo de cada rota ou o motivo de pular. */
export function passosDaLinha(l: AgendaGradeLinha, e: EstadoLote, ctx: ContextoLote): PassoLote[] {
  const tipo = tipoAgendamento(l);
  const geral = motivoLinhaLote(l, ctx.supervisor);
  const pular = (campo: CampoLote, motivo: string, igual = false): PassoLote => ({ campo, corpo: null, motivo, ...(igual ? { igual } : {}) });
  const enviar = (campo: CampoLote, corpo: CorpoLote): PassoLote => ({ campo, corpo, motivo: null });

  return camposEscolhidos(e).map((campo): PassoLote => {
    if (geral) return pular(campo, geral);
    switch (campo) {
      case 'especialidade': {
        if (tipo !== 'CONSULTA') return pular(campo, 'Só consulta');
        const trava = motivoTravaSlot(l);
        if (trava) return pular(campo, trava);
        if (l.especialidade?.cdEspecialidadeAgend === e.cdEspecialidade) return pular(campo, 'Já é a especialidade atual', true);
        return enviar(campo, { tipo, cdEspecialidade: e.cdEspecialidade! });
      }
      case 'procedimento': {
        if (tipo !== 'EXAME') return pular(campo, 'Só exame');
        const trava = motivoTravaSlot(l);
        if (trava) return pular(campo, trava);
        if (l.nrSeqProcInterno === e.nrSeqProcInterno) return pular(campo, 'Já é o exame atual', true);
        return enviar(campo, { tipo, nrSeqProcInterno: e.nrSeqProcInterno! });
      }
      case 'status':
        return enviar(campo, { tipo, ieStatusAgenda: e.status! });
      case 'classificacao':
        return enviar(campo, { tipo, ieClassifAgenda: e.classif! });
      case 'tipoAtendimento':
        if (tipo !== 'CONSULTA') return pular(campo, 'Só consulta');
        return enviar(campo, { ieTipoAtendimento: e.tipoAtendimento });
      case 'tipoConsulta':
        if (tipo !== 'CONSULTA') return pular(campo, 'Só consulta');
        return enviar(campo, { nrSeqTipoConsulta: e.tipoConsulta === 'limpar' ? null : TIPO_CONSULTA_REAGENDAMENTO_ONLINE });
      case 'convenio': {
        if (e.cdConvenio === CONVENIO_CARTAO) {
          const p = planoDaLinha224(l, ctx.planos);
          if (p.motivo) return pular(campo, p.motivo);
          return enviar(campo, { tipo, cdConvenio: CONVENIO_CARTAO, cdCategoria: p.cdCategoria });
        }
        return enviar(campo, { tipo, cdConvenio: e.cdConvenio!, cdCategoria: e.cdCategoria });
      }
      case 'observacao':
        if (e.observacao === (l.dsObservacao ?? '')) return pular(campo, 'Igual à atual', true);
        return enviar(campo, { tipo, dsObservacao: e.observacao });
    }
  });
}

// --- Resultado por linha ------------------------------------------------------------------------

export type SituacaoItem = 'ok' | 'erro' | 'pulado' | 'igual';

export interface ResultadoItem {
  campo: string;
  rotulo: string;
  situacao: SituacaoItem;
  mensagem: string;
}

export interface ResultadoLinha {
  linha: AgendaGradeLinha;
  itens: ResultadoItem[];
}

export interface ResumoResultado {
  ok: number;
  erro: number;
  pulado: number;
}

/** Contagem de campos gravados, recusados e pulados ("igual" não conta: nada a fazer). */
export function resumirResultados(r: ResultadoLinha[]): ResumoResultado {
  const c: ResumoResultado = { ok: 0, erro: 0, pulado: 0 };
  for (const l of r) {
    for (const i of l.itens) {
      if (i.situacao === 'ok') c.ok++;
      else if (i.situacao === 'erro') c.erro++;
      else if (i.situacao === 'pulado') c.pulado++;
    }
  }
  return c;
}
